import React, { useEffect, useRef, useState, useMemo } from "react";
import { askQuestion, giveFeedback } from "../../../utilities/EmgptUtilities";
import { Button, FormControl, Alert } from "react-bootstrap";
import ChatOverlayFrame from "../../Common/ChatOverlayFrame";
import { FaCopy, FaPen, FaRedo } from "react-icons/fa";
import { fetchChatSuggestions } from "../../../utilities/DocAssistantUtilities";
import useChatVoice, { htmlToSpeechText } from "../../../utilities/useChatVoice";
import TradingViewChart from "../../TradingView/TradingViewChart";
import { capitalize } from "../DashboardUtils";
import ReportAnalysis from "./ReportAnalysis";
// Same renderer as the CoTrader assistant: analysis steps, sources, charts.
import { RenderFromString } from "../../CoTrader/CoTraderEmgpt";
import { SuggestionsSkeleton } from "../../CoTrader/CoTraderSkeletons";
import { useResponsiveTileTypography } from "../../CoTrader/Utilities/useResponsiveTileTypography";
import "../../Common/chatSurface.css";
import "../../CoTrader/CoTrader.css";
import "../ProductDashboard.css";
import {
  normalizeChatSuggestion,
  normalizeChatSuggestions,
} from "../../../utilities/chatSuggestionUtilities";
import { useResolvedDark } from "../../../ThemeProvider/ThemeContext";

const VOICE_IDLE_AUTO_SEND_MS = 1000;
const TEST_SUMMARIZE_PROMPTS = [
  "Summarize the company profile",
  "Summarize key statistics and valuation",
  "Summarize the company's financial performance",
  "Summarize analyst expectations and outlook",
  "Give me a complete company overview",
];
const normalizeDashboardIdentifier = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const EmgptDiv = ({
  documentIds,
  dashboardName,
  dashboardTitle,
  priceTicker,
  showPriceOverlay,
  selectedFilters,
  reference_ids,
  searchFlag,
  setSearchFlag,
  default_embedding,

  messages,
  setMessages,
  userInput,
  setUserInput,
  isLoading,
  setIsLoading,
  showSuggestions,
  dynamicSuggestions,
  setDynamicSuggestions,
  isStopped,
  setIsStopped,
  isStoppedRef,
  wsRef,
  selectedNews,
  newsList,
  // tabs,
  // setTabs,
  // setTvTickers,
  selectedDate,
  selectedPoint,
  point_click_news_lookback_days,
  resetDate,
  isDark: isDarkProp,
  isOverlayOpen: controlledOverlayOpen,
  onOverlayOpenChange,
  hasAskedSummarizePrompt,
  setHasAskedSummarizePrompt,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const theme = useMemo(
    () =>
      isDark
        ? {
            pageBg: "#0B1020",
            panelBg: "#0F1424",
            panelBg2: "#121A2E",
            border: "#232B4A",
            text: "#E8ECF8",
            muted: "#A6B0CF",
            chipBg: "#111A33",
            inputBg: "#0F1424",
            btnOutline: "#2A3560",
          }
        : {
            pageBg: "#FFFFFF",
            panelBg: "#FFFFFF",
            panelBg2: "#F8F9FA",
            border: "#E9ECEF",
            text: "#0B1220",
            muted: "#6C757D",
            chipBg: "#F1F5FF",
            inputBg: "#FFFFFF",
            btnOutline: "#CED4DA",
          },
    [isDark]
  );
  const chatSurfaceVars = useMemo(
    () => ({
      "--ea-chat-page-bg": theme.pageBg,
      "--ea-chat-surface": theme.panelBg,
      "--ea-chat-surface-alt": isDark ? theme.panelBg2 : "#F7FBFF",
      "--ea-chat-border": theme.border,
      "--ea-chat-text": theme.text,
      "--ea-chat-muted": theme.muted,
      "--ea-chat-link": isDark ? "#7DA0FF" : "#0D6EFD",
      "--ea-chat-accent": isDark ? "#4C7DFF" : "#0D6EFD",
      "--ea-chat-accent-soft": isDark
        ? "rgba(76, 125, 255, 0.18)"
        : "rgba(13, 110, 253, 0.12)",
      "--ea-chat-question-bg": isDark
        ? "linear-gradient(135deg, #3158DC 0%, #567EFF 100%)"
        : "linear-gradient(135deg, #0D6EFD 0%, #175CD3 100%)",
      "--ea-chat-question-text": "#FFFFFF",
      "--ea-chat-answer-bg": isDark ? "rgba(15, 20, 36, 0.92)" : "rgba(255, 255, 255, 0.94)",
      "--ea-chat-answer-text": theme.text,
      "--ea-chat-body-bg": isDark
        ? "linear-gradient(180deg, #0F1424 0%, #121A2E 100%)"
        : "linear-gradient(180deg, #F7FBFF 0%, #FFFFFF 52%)",
      "--ea-chat-composer-bg": isDark
        ? "linear-gradient(180deg, rgba(18, 26, 46, 0.98) 0%, rgba(15, 20, 36, 1) 100%)"
        : "linear-gradient(180deg, #FBFDFF 0%, #F5F8FC 100%)",
      "--ea-chat-input-bg": theme.inputBg,
      "--ea-chat-chip-bg": theme.chipBg,
      "--ea-chat-ghost-bg": isDark ? "rgba(15, 20, 36, 0.78)" : "rgba(255, 255, 255, 0.88)",
      "--ea-chat-toast-bg": isDark ? "#111A33" : "#0B1220",
      "--ea-chat-shadow": isDark
        ? "0 26px 72px rgba(0, 0, 0, 0.40)"
        : "0 22px 60px rgba(16, 24, 40, 0.10)",
    }),
    [isDark, theme]
  );

  const [documentIdInput] = useState("");
  const messagesEndRef = useRef(null);
  const userLanguage = localStorage.getItem("user_language") || "English";

  const {
    clearSpeechError,
    isListening,
    isSpeechRecognitionSupported,
    isSpeechSynthesisSupported,
    isTtsEnabled,
    resetVoiceInput,
    speakStreamingText,
    speechError,
    stopListening,
    stopSpeaking,
    toggleListening,
    toggleTts,
  } = useChatVoice({
    userInput,
    setUserInput,
    userLanguage,
  });

  const [filterCountries, setFilterCountries] = useState([]);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [ragRatioReq, setRagRatioReq] = useState(true);
  const [activeTvSymbol, setActiveTvSymbol] = useState(null);
  const [feedbackMessageId, setFeedbackMessageId] = useState([]);
  const [feedbackTexts, setFeedbackTexts] = useState({});
  const [feedbackTypes, setFeedbackTypes] = useState({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const { containerRef: chatShellRef, tier: chatTier } = useResponsiveTileTypography();
  const [summary, setSummary] = useState("");
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [internalIsOverlayOpen, setInternalIsOverlayOpen] = useState(false);
  const [activeAssistantTab, setActiveAssistantTab] = useState("chat");
  const selectedCompanyCount = Array.isArray(selectedFilters?.filter3)
    ? selectedFilters.filter3.length
    : 0;
  const selectedCompany =
    selectedCompanyCount === 1
      ? selectedFilters.filter3[0]?.value || selectedFilters.filter3[0]?.label || ""
      : "";
  // ReportGPT accepts the selected CompanyMonitor reference ID as `company_id`.
  // Normal EM GPT sends this same source identity in its `reference_ids` array.
  const reportCompanyId = Array.isArray(reference_ids)
    ? reference_ids.find((referenceId) => Boolean(referenceId)) || ""
    : "";
  const isCompanyMonitor = [dashboardName, dashboardTitle].some(
    (value) => normalizeDashboardIdentifier(value) === "companymonitor",
  );
  const isPrivateCompanyMonitor = [dashboardName, dashboardTitle].some(
    (value) =>
      normalizeDashboardIdentifier(value).includes("privatecompanymonitor") ||
      normalizeDashboardIdentifier(value) === "stocksmainprivate",
  );
  const privateCompanySlug = isPrivateCompanyMonitor
    ? String(selectedCompany).toLowerCase().replace(/\s/g, "")
    : "";
  const normalEmgptTarget = isPrivateCompanyMonitor && privateCompanySlug
    ? { slug: privateCompanySlug }
    : isCompanyMonitor && String(priceTicker || "").trim()
      ? { ticker: priceTicker.trim() }
      : {};
  const canShowReportAnalysis =
    isCompanyMonitor &&
    selectedCompanyCount === 1 &&
    Boolean(String(priceTicker || "").trim());
  const isOverlayOpen =
    typeof controlledOverlayOpen === "boolean"
      ? controlledOverlayOpen
      : internalIsOverlayOpen;

  const setOverlayOpen = (nextValue) => {
    const resolvedValue =
      typeof nextValue === "function" ? nextValue(isOverlayOpen) : nextValue;

    if (typeof controlledOverlayOpen !== "boolean") {
      setInternalIsOverlayOpen(resolvedValue);
    }

    onOverlayOpenChange?.(resolvedValue);
  };

  useEffect(() => {
    if (!canShowReportAnalysis && activeAssistantTab === "report") {
      setActiveAssistantTab("chat");
    }
  }, [activeAssistantTab, canShowReportAnalysis]);

  // optional: small toast for copy
  const [copiedToast, setCopiedToast] = useState(false);
  const voiceAutoSendTimerRef = useRef(null);
  const sendUserQuestionRef = useRef(null);

  const [sources] = useState([
    { label: "NEWS", value: "NEWS" },
    { label: "SENTIMENT", value: "SENTIMENT" },
  ]);

  const getFormattedFilterValues = (filters) => {
    const values = filters.map((x) => "" + x.value);
    if (values.length > 3) return values.slice(0, 3).join(", ") + ", ...";
    return values.join(", ");
  };

  const formattedFilters = getFormattedFilterValues(selectedFilters.filter3);

  const chatSuggestionsList = {
    default: [
      ...(reference_ids.length < 2 && formattedFilters
        ? [`What are the implications for ${formattedFilters} based on this news flow?`]
        : []),
      "Are there any investment opportunities based on this news flow?",
      "What are the implications for the stock market based on this news flow?",
      "Summarize the information flow on the selected themes over the chosen date range.",
      "Which asset classes (stocks, bonds, or foreign exchange) look the most attractive for investors?",
      "Are there any risks that investors should be aware of?",
      "What is the correlation between the news flow and the stock market?",
      "What is the impact of the news flow on the stock market?",
    ],
    stocks_main: [
      ...(reference_ids.length < 2 && formattedFilters
        ? [`What are the implications for ${formattedFilters} based on this news flow?`]
        : []),
      ...(formattedFilters
        ? [`What are the key risks for ${formattedFilters} in the selected time period?`]
        : []),
      "What are the key risks flagged for this company in the selected period?",
      "How has public sentiment toward the company changed over time?",
      "What specific issues have been reported in this company’s supply chain?",
      "Are there any legal or regulatory developments impacting this company recently?",
      "Which financial risk themes are driving the most negative coverage?",
      "How does this company’s recent news compare to its competitors in the same country?",
    ],
  };
  const hasMultipleKeywordThemes =
    (selectedFilters?.filter3?.length || 0) > 1;

  const [chatSuggestions, setChatSuggestions] = useState(() =>
    normalizeChatSuggestions(
      chatSuggestionsList[dashboardName] || chatSuggestionsList.default,
    ),
  );
  const [apiChatSuggestions, setApiChatSuggestions] = useState([]);
  const summarizePrompts = useMemo(
    () => normalizeChatSuggestions(TEST_SUMMARIZE_PROMPTS),
    [],
  );
  const canShowSummarizePrompts = Boolean(
    (showPriceOverlay && isCompanyMonitor) ||
      (isPrivateCompanyMonitor && privateCompanySlug),
  );
  const areSummarizePromptsVisible =
    canShowSummarizePrompts && !hasAskedSummarizePrompt;
  const lastAnswerForSpeech = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.type !== "answer") continue;
      return {
        text: message?.text || "",
        streamKey: message?.message_id || `answer-${i}`,
      };
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (selectedFilters?.filter2 && selectedFilters?.filter3) {
      setFilterCountries(selectedFilters.filter2.map((x) => x.value));
      setFilterCompanies(selectedFilters.filter3?.map((x) => x.value));
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (!isTtsEnabled || !lastAnswerForSpeech) return;
    const textForSpeech = htmlToSpeechText(lastAnswerForSpeech.text);
    speakStreamingText(textForSpeech, {
      isFinal: !isLoading,
      streamKey: lastAnswerForSpeech.streamKey,
    });
  }, [isLoading, isTtsEnabled, lastAnswerForSpeech, speakStreamingText]);

  useEffect(() => {
    return () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
      stopListening();
      stopSpeaking();
    };
  }, [stopListening, stopSpeaking]);

  useEffect(() => {
    const el = messagesEndRef.current;
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [isLoading]);

  useEffect(() => {
    isStoppedRef.current = isStopped;
  }, [isStopped]);

  useEffect(() => {
    if (messages.length === 0) {
      setSummary("");
      setSummaryVersion(0);
    }
  }, [messages.length]);

  useEffect(() => {
    const updatedSuggestions =
      chatSuggestionsList[dashboardName] || chatSuggestionsList.default;

    const replaced = normalizeChatSuggestions(updatedSuggestions).map(
      (suggestion) => ({
        ...suggestion,
        question: suggestion.question.replace(
          /{.*?}/g,
          selectedFilters?.filter3?.map((x) => x.value).join(", ") || "",
        ),
      }),
    );
    setChatSuggestions(replaced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters?.filter3, dashboardName]);

  // Multi-theme selections prefer API suggestions, but fall back to the
  // static list once loading finishes empty (no documents, API error, or
  // no suggestions returned) so the panel is never blank.
  const standardStarterPrompts = useMemo(() => {
    if (hasMultipleKeywordThemes) {
      if (apiChatSuggestions.length > 0) return apiChatSuggestions.slice(0, 4);
      if (suggestionsLoading) return [];
    }
    return chatSuggestions.slice(0, 4);
  }, [
    apiChatSuggestions,
    chatSuggestions,
    hasMultipleKeywordThemes,
    suggestionsLoading,
  ]);
  // Same set whether or not a question is running, so the panel never
  // reflows when asking starts.
  const visibleStarterPrompts = areSummarizePromptsVisible
    ? summarizePrompts
    : standardStarterPrompts;

  useEffect(() => {
    // The suggestions API only knows equity and macro framings. Company
    // monitors get the equity one; every other monitor (Macro, Commodity, ...)
    // reads as macro news flow.
    const isEquityMonitor =
      isCompanyMonitor ||
      isPrivateCompanyMonitor ||
      normalizeDashboardIdentifier(dashboardName) === "stocksmain";
    const origin = isEquityMonitor
      ? "emgpt_COMPANY_MONITOR"
      : "emgpt_MACRO_MONITOR";

    const fallbackSuggestions = normalizeChatSuggestions(
      chatSuggestionsList[dashboardName] || chatSuggestionsList.default,
    ).map((suggestion) => ({
      ...suggestion,
      question: suggestion.question.replace(
        /{.*?}/g,
        selectedFilters?.filter3?.map((x) => x.value).join(", ") || "",
      ),
    }));

    setApiChatSuggestions([]);

    if (documentIds.length > 0) {
      let isCurrent = true;
      setSuggestionsLoading(true);
      fetchChatSuggestions(
        documentIds,
        (apiSuggestions) => {
          if (!isCurrent) return;
          setApiChatSuggestions(apiSuggestions);
          setChatSuggestions([
            ...fallbackSuggestions.slice(0, 2),
            ...apiSuggestions,
          ]);
        },
        origin,
        [""],
        []
      ).finally(() => {
        if (isCurrent) setSuggestionsLoading(false);
      });
      return () => {
        isCurrent = false;
      };
    }
    setSuggestionsLoading(false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentIds,
    dashboardName,
    selectedFilters?.filter3,
    dashboardTitle,
    isCompanyMonitor,
    isPrivateCompanyMonitor,
  ]);

  const handleInputChange = (e) => setUserInput(e.target.value);

  const handleKeyDown = (e) => {
    setRagRatioReq(false);
    if (e.key === "Enter") sendUserQuestion(userInput);
  };

  const handleFeedback = (messageId, feedback, feedbackText) => {
    giveFeedback({
      msgId: messageId,
      feedback,
      feedbackText,
      setFeedbackMessageId,
    });
  };

  const handleFeedbackTextChange = (messageId, text) => {
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: text }));
  };

  const clearPendingFeedback = (messageId) => {
    setFeedbackTexts((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
    setFeedbackTypes((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
  };

  const openFeedbackForm = (messageId, feedbackType) => {
    setFeedbackTypes((prev) => ({ ...prev, [messageId]: feedbackType }));
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: "" }));
  };

  const submitFeedback = (messageId) => {
    const feedback = feedbackTypes[messageId] || "Negative";
    const feedbackText = feedbackTexts[messageId]?.trim() || feedback;
    handleFeedback(messageId, feedback, feedbackText);
    clearPendingFeedback(messageId);
  };

  const getFeedbackPlaceholder = (messageId) =>
    feedbackTypes[messageId] === "Positive"
      ? "Tell us what worked well..."
      : "Tell us what went wrong...";

  const sendUserQuestion = (suggestion, { useSummarizeGpt = false } = {}) => {
    clearSpeechError();
    stopListening();
    stopSpeaking();
    const normalizedSuggestion = normalizeChatSuggestion(suggestion);
    if (!normalizedSuggestion) return;
    setHasAskedSummarizePrompt(true);
    const { question, evidence_refs: evidenceRefs } = normalizedSuggestion;
    resetVoiceInput();
    setIsStopped(false);
    isStoppedRef.current = false;
    askQuestion(
      setIsLoading,
      question,
      setUserInput,
      documentIdInput,
      documentIds,
      setMessages,
      messages,
      null,
      null,
      dashboardName,
      filterCountries,
      filterCompanies,
      reference_ids,
      searchFlag,
      isStoppedRef,
      setIsStopped,
      sources,
      default_embedding,
      setDynamicSuggestions,
      showSuggestions,
      wsRef,
      selectedNews,
      newsList,
      null,
      ragRatioReq,
      // tabs,
      // setTabs,
      // setTvTickers,
      selectedPoint,
      selectedDate,
      point_click_news_lookback_days,
      summary,
      summaryVersion,
      setSummary,
      setSummaryVersion,
      useSummarizeGpt
        ? {
            evidenceRefs,
            requestMode: "SUMMARIZEGPT",
            ticker: priceTicker,
            slug: privateCompanySlug,
          }
        : { evidenceRefs, ...normalEmgptTarget }
    );
  };

  const handleSuggestionClick = (suggestion) => sendUserQuestion(suggestion);
  const handleSummarizePromptClick = (suggestion) =>
    sendUserQuestion(suggestion, { useSummarizeGpt: true });
  sendUserQuestionRef.current = sendUserQuestion;

  useEffect(() => {
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }

    if (!isListening || isLoading) return;

    const trimmedVoiceInput = (userInput || "").trim();
    if (!trimmedVoiceInput) return;

    voiceAutoSendTimerRef.current = setTimeout(() => {
      stopListening();
      sendUserQuestionRef.current?.(trimmedVoiceInput);
      voiceAutoSendTimerRef.current = null;
    }, VOICE_IDLE_AUTO_SEND_MS);

    return () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
    };
  }, [isListening, isLoading, stopListening, userInput]);

  const ClearChat = () => {
    clearSpeechError();
    stopListening();
    stopSpeaking();
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }
    resetVoiceInput();
    setMessages([]);
    setFeedbackTexts({});
    setFeedbackTypes({});
    setSummary("");
    setSummaryVersion(0);
    setHasAskedSummarizePrompt(false);
    // setTabs([]);
  };

  const copyToClipboard = async (htmlString) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      doc.querySelectorAll("collapsiblediv, linkpreview").forEach((el) => el.remove());
      const cleanedHTML = doc.body.innerText || doc.body.textContent || "";
      await navigator.clipboard.writeText(cleanedHTML);
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 1200);
    } catch (e) {
      console.error("Clipboard error", e);
    }
  };

  const regenerateAnswer = (index, realTime) => {
    const questionToRegenerate = messages[index - 1]?.text || "";
    setSearchFlag(realTime);
    sendUserQuestion(questionToRegenerate);
  };

  const handleActionIconKeyDown = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  const editQuestion = (index) => {
    setUserInput(messages[index - 1]?.text || "");
    document.getElementById("userInput")?.focus();
  };

  const stopGenerating = () => {
    stopListening();
    stopSpeaking();
    setIsStopped(true);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsLoading(false);
  };

  const assistantLabel =
    dashboardName === "defense_main_clean" ? "Sentinel GPT" : "EM GPT";
  const selectedDateLabel =
    selectedDate instanceof Date ? selectedDate.toLocaleDateString("en-US") : "";
  const selectedPointLabel = selectedPoint
    ? [selectedPoint.name, capitalize(selectedPoint.country)]
        .filter(Boolean)
        .join(" · ")
    : "";
  const renderSuggestion = (suggestion, index, onClick) => (
    <button
      type="button"
      className="ct-suggestion"
      key={`${suggestion.question}-${index}`}
      title={suggestion.question}
      onClick={() => onClick(suggestion)}
    >
      <span>{suggestion.question}</span>
    </button>
  );

  return (
    <ChatOverlayFrame
      isOpen={isOverlayOpen}
      onClose={() => setOverlayOpen(false)}
    >
      <div
        ref={chatShellRef}
        data-tier={chatTier}
        data-theme={isDark ? "dark" : "light"}
        className="d-flex flex-column w-100 h-100 ea-chat-surface ea-chat-shell ct-chat pd-chat"
        style={chatSurfaceVars}
      >
      <div className="ea-chat-header d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          <div className="ea-chat-title-group">
            <span className="ea-chat-title">
              Ask EMAlpha
              <span className="ct-chat-live">{assistantLabel}</span>
            </span>
            <p className="ea-chat-subtitle mb-0">
              {dashboardTitle
                ? `Grounded in the ${dashboardTitle} information flow.`
                : "Grounded in the active information flow."}
            </p>
          </div>
        </div>

        <div className="ea-chat-header-actions">
          <Button
            type="button"
            onClick={() => setOverlayOpen((prev) => !prev)}
            className="btn btn-sm ea-chat-ghost-btn"
            title={isOverlayOpen ? "Exit full screen" : "Open full screen"}
            aria-pressed={isOverlayOpen}
          >
            <i
              className={`fa-solid ${
                isOverlayOpen ? "fa-compress" : "fa-expand"
              } me-2`}
            />
            {isOverlayOpen ? "Exit" : "Expand"}
          </Button>
          {messages?.length > 0 && (
            <Button
              onClick={ClearChat}
              className="btn btn-sm ea-chat-clear-btn"
              disabled={isLoading}
            >
              Clear Chat
            </Button>
          )}
        </div>
      </div>

      {canShowReportAnalysis && (
        <div className="ct-tabs pd-chat-tabs" role="tablist" aria-label="Assistant mode">
          <button
            type="button"
            className={`ct-tab ${activeAssistantTab === "chat" ? "is-active" : ""}`}
            onClick={() => setActiveAssistantTab("chat")}
            role="tab"
            aria-selected={activeAssistantTab === "chat"}
          >
            Chat
          </button>
          <button
            type="button"
            className={`ct-tab ${activeAssistantTab === "report" ? "is-active" : ""}`}
            onClick={() => setActiveAssistantTab("report")}
            role="tab"
            aria-selected={activeAssistantTab === "report"}
          >
            Report &amp; analysis
          </button>
        </div>
      )}

      {canShowReportAnalysis && (
        <div
          className={`ea-chat-content-panel ${
            activeAssistantTab === "report" ? "d-flex" : "d-none"
          }`}
        >
          <ReportAnalysis
            isDark={isDark}
            acquirerCompany={selectedCompany}
            acquirerTicker={priceTicker}
            dashboardName={dashboardName}
            companyId={reportCompanyId}
            HtmlRenderer={RenderFromString}
          />
        </div>
      )}

      <div
        className={`ea-chat-content-panel flex-column ${
          activeAssistantTab === "chat" ? "d-flex" : "d-none"
        }`}
      >
      {activeTvSymbol && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 1050, background: "rgba(0,0,0,0.45)" }}
          onClick={() => setActiveTvSymbol(null)}
        >
          <div
            className="rounded shadow"
            style={{
              width: "96%",
              height: "86%",
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.panelBg,
              color: theme.text,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="d-flex align-items-center justify-content-between p-2 border-bottom"
              style={{ borderColor: theme.border }}
            >
              <div className="fw-semibold">{activeTvSymbol}</div>
              <Button size="sm" variant="outline-secondary" onClick={() => setActiveTvSymbol(null)}>
                Close
              </Button>
            </div>
            <div style={{ width: "100%", height: "calc(100% - 48px)" }}>
              <TradingViewChart symbol={activeTvSymbol} dark={!!isDark} />
            </div>
          </div>
        </div>
      )}

      <div
        className="flex-grow-1 overflow-y-auto ea-chat-scroll"
        style={{ overflowX: "hidden" }}
      >
        {copiedToast && (
          <div
            className="position-fixed start-50 translate-middle-x px-3 py-2 ea-chat-toast"
            style={{ bottom: 88, zIndex: 9999, fontSize: "0.85rem" }}
          >
            Copied
          </div>
        )}

        {messages.length === 0 ? (
          <div className="ea-chat-empty">
            {areSummarizePromptsVisible && (
              <div className="ct-label ct-suggestions-label">Company summaries</div>
            )}
            {visibleStarterPrompts.length > 0 ? (
              <div className="ct-suggestions" id="suggestion_section">
                {visibleStarterPrompts.map((suggestion, index) =>
                  renderSuggestion(
                    suggestion,
                    index,
                    areSummarizePromptsVisible
                      ? handleSummarizePromptClick
                      : handleSuggestionClick
                  )
                )}
              </div>
            ) : suggestionsLoading ? (
              <SuggestionsSkeleton />
            ) : null}
          </div>
        ) : (
          <div>
            {messages.map((msg, index) => {
              if (!msg || typeof msg.type === "undefined") return null;

              const isQuestion = msg.type === "question";

              return (
                <Alert
                  key={index}
                  className={`my-2 ea-chat-bubble ${
                    isQuestion ? "ea-chat-bubble--question" : "ea-chat-bubble--answer"
                  }`}
                  style={{
                    maxWidth: isQuestion ? "70%" : "100%",
                    marginRight: isQuestion ? "0" : "auto",
                    marginLeft: isQuestion ? "auto" : "0",
                  }}
                >
                  <div className="ea-chat-rich">
                    <RenderFromString
                      htmlString={msg.text}
                      sources={msg.sources}
                      isStreaming={
                        isLoading &&
                        msg.type === "answer" &&
                        index === messages.length - 1
                      }
                    />
                  </div>

                  {msg.type === "answer" && !isLoading && (
                    <div className="ea-chat-actions">
                      <div className="ea-chat-action-group">
                        {msg.message_id && !feedbackMessageId.includes(msg.message_id) && (
                          <>
                            <Button
                              className="ea-chat-icon-btn"
                              variant="outline-success"
                              size="sm"
                              onClick={() => openFeedbackForm(msg.message_id, "Positive")}
                            >
                              <i className="fa-solid fa-thumbs-up" />
                            </Button>
                            <Button
                              className="ea-chat-icon-btn"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => openFeedbackForm(msg.message_id, "Negative")}
                            >
                              <i className="fa-solid fa-thumbs-down" />
                            </Button>
                          </>
                        )}
                      </div>

                      <div className="ea-chat-action-group ms-auto">
                        <span
                          className="ea-chat-action-link"
                          role="button"
                          tabIndex={0}
                          onClick={() => copyToClipboard(msg.text)}
                          onKeyDown={(event) =>
                            handleActionIconKeyDown(event, () => copyToClipboard(msg.text))
                          }
                          title="Copy Message"
                        >
                          <FaCopy aria-hidden="true" />
                        </span>
                        <span
                          className="ea-chat-action-link"
                          role="button"
                          tabIndex={0}
                          title="Edit Message"
                          onClick={() => editQuestion(index)}
                          onKeyDown={(event) =>
                            handleActionIconKeyDown(event, () => editQuestion(index))
                          }
                        >
                          <FaPen aria-hidden="true" />
                        </span>
                        <span
                          className="ea-chat-action-link"
                          role="button"
                          tabIndex={0}
                          title="Regenerate Answer"
                          onClick={() => regenerateAnswer(index, searchFlag)}
                          onKeyDown={(event) =>
                            handleActionIconKeyDown(event, () =>
                              regenerateAnswer(index, searchFlag)
                            )
                          }
                        >
                          <FaRedo aria-hidden="true" />
                        </span>
                      </div>
                    </div>
                  )}

                  {feedbackTexts[msg.message_id] !== undefined && (
                    <div className="mt-2 w-100">
                      <FormControl
                        className="ea-chat-feedback-textarea"
                        as="textarea"
                        rows={2}
                        placeholder={getFeedbackPlaceholder(msg.message_id)}
                        value={feedbackTexts[msg.message_id]}
                        onChange={(e) =>
                          handleFeedbackTextChange(msg.message_id, e.target.value)
                        }
                      />
                      <Button
                        size="sm"
                        className="mt-1 ea-chat-feedback-submit"
                        onClick={() => submitFeedback(msg.message_id)}
                      >
                        Submit Feedback
                      </Button>
                    </div>
                  )}
                </Alert>
              );
            })}

            {areSummarizePromptsVisible && (
              <>
                <div className="ct-label ct-suggestions-label">Company summaries</div>
                <div className="ct-suggestions ct-suggestions--compact">
                  {summarizePrompts.map((suggestion, index) =>
                    renderSuggestion(suggestion, index, handleSummarizePromptClick)
                  )}
                </div>
              </>
            )}

            {dynamicSuggestions.length > 0 && !isLoading && (
              <div className="ct-suggestions ct-suggestions--compact">
                {dynamicSuggestions.map((suggestion, index) =>
                  renderSuggestion(suggestion, index, handleSuggestionClick)
                )}
              </div>
            )}
            {/* Progress lives in the analysis-steps header, which shows the
                live step and an elapsed timer while the answer streams. */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="w-100 ea-composer is-docked">
        {speechError && <div className="ct-chat-hint is-error">{speechError}</div>}
        {isListening && !isLoading && (
          <div className="ct-chat-hint">
            Listening... auto-send after {VOICE_IDLE_AUTO_SEND_MS / 1000}s of silence.
          </div>
        )}
        {selectedPoint && (
          <div className="pd-chat-context">
            <i className="fa-solid fa-crosshairs" aria-hidden="true" />
            <span>
              Focused on <strong>{selectedPointLabel}</strong>
              {selectedDateLabel ? ` · ${selectedDateLabel}` : ""}
            </span>
            <button
              type="button"
              className="pd-chat-context-clear"
              onClick={resetDate}
              title="Clear date focus"
              aria-label="Clear date focus"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>
        )}
        {!selectedPoint && selectedNews && (
          <div className="pd-chat-context">
            <i className="fa-regular fa-file-lines" aria-hidden="true" />
            <span>Answers use the open article as context.</span>
          </div>
        )}
        <div className="ea-composer__field">
          <div className="ea-composer__input-wrap">
            <input
              type="text"
              placeholder={`Ask about ${dashboardTitle || "this information flow"}`}
              aria-label="Type your question"
              id="userInput"
              className="form-control ea-composer__input"
              value={userInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoComplete="off"
            />
          </div>
          {isSpeechRecognitionSupported && (
            <button
              type="button"
              className={`ea-composer__icon ${isListening ? "is-active" : ""}`}
              onClick={toggleListening}
              title={isListening ? "Stop voice input" : "Voice input"}
              aria-label={isListening ? "Stop voice input" : "Start voice input"}
              aria-pressed={isListening}
              disabled={isLoading}
            >
              <i className="fa-solid fa-microphone" aria-hidden="true" />
            </button>
          )}
          {isSpeechSynthesisSupported && (
            <button
              type="button"
              className="ea-composer__icon"
              onClick={toggleTts}
              title={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
              aria-label={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
              aria-pressed={isTtsEnabled}
              style={isTtsEnabled ? { color: "#067647" } : undefined}
            >
              <i
                className={`fa-solid ${isTtsEnabled ? "fa-volume-high" : "fa-volume-xmark"}`}
                aria-hidden="true"
              />
            </button>
          )}
          {isLoading ? (
            <button
              type="button"
              className="ea-composer__send is-stop"
              title="Stop generating"
              aria-label="Stop generating"
              onClick={stopGenerating}
            >
              <span className="ea-composer__stop-glyph" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="ea-composer__send"
              onClick={() => sendUserQuestion(userInput)}
              disabled={!(userInput ?? "").trim()}
              title="Send"
              aria-label="Send message"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      </div>
      </div>
    </ChatOverlayFrame>
  );
};

export { RenderFromString };

export default EmgptDiv;
